import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import apiClient from '../api/api';

function OwnerDetailsPage() {
  const [name, setName] = useState('');
  const [organisation, setOrganisation] = useState('');
  const [contact_No, setContact_No] = useState('');
  const [nid_or_passport, setNid_or_passport] = useState('');
  const [profilePicture, setProfilePicture] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // Address states
  const [selectedDivision, setSelectedDivision] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedUpazila, setSelectedUpazila] = useState('');
  const [granularLocation, setGranularLocation] = useState('');
  const [postCode, setPostCode] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');

  // Data states for dropdowns
  const [divisions, setDivisions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [upazilas, setUpazilas] = useState([]);
  
  // Loading states
  const [divisionsLoading, setDivisionsLoading] = useState(true);
  const [districtsLoading, setDistrictsLoading] = useState(false);
  const [upazilasLoading, setUpazilasLoading] = useState(false);

  const userId = location.state?.userId;

  // Fetch initial data (Divisions)
  useEffect(() => {
    const fetchDivisions = async () => {
      try {
        setDivisionsLoading(true);
        const response = await apiClient.get('/location-data/divisions');
        setDivisions(['', ...response.data]);
      } catch (error) {
        console.error("Failed to fetch divisions:", error);
      } finally {
        setDivisionsLoading(false);
      }
    };
    fetchDivisions();
  }, []);

  // Fetch districts when division changes
  useEffect(() => {
    if (!selectedDivision) {
      setDistricts([]);
      setSelectedDistrict('');
      return;
    }
    const fetchDistricts = async () => {
      setDistrictsLoading(true);
      try {
        const response = await apiClient.get(`/location-data/districts/${selectedDivision}`);
        setDistricts(['', ...response.data]);
      } catch (error) { console.error(error); } 
      finally { setDistrictsLoading(false); }
    };
    fetchDistricts();
  }, [selectedDivision]);

  // Fetch upazilas when district changes
  useEffect(() => {
    if (!selectedDistrict) {
      setUpazilas([]);
      setSelectedUpazila('');
      return;
    }
    const fetchUpazilas = async () => {
      setUpazilasLoading(true);
      try {
        const response = await apiClient.get(`/location-data/thanas/${selectedDistrict}`);
        setUpazilas(['', ...response.data]);
      } catch (error) { console.error(error); } 
      finally { setUpazilasLoading(false); }
    };
    fetchUpazilas();
  }, [selectedDistrict]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePicture(file);
    } else {
      setProfilePicture(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userId) {
      setError('User session expired or invalid. Please register again.');
      return;
    }

    const formData = new FormData();
    formData.append('userId', userId);
    formData.append('name', name);
    formData.append('organisation', organisation);
    formData.append('contact_No', contact_No);
    formData.append('nid_or_passport', nid_or_passport);

    // Structured location data
    const locationObj = {
      division: selectedDivision,
      district: selectedDistrict,
      upazila: selectedUpazila,
      granular_location: granularLocation,
      postCode,
      latitude,
      longitude,
    };
    formData.append('location', JSON.stringify(locationObj));

    if (profilePicture) {
      formData.append('profilePicture', profilePicture);
    }

    try {
      // This should eventually post to a specific profile endpoint
      const response = await apiClient.post('/owner-details', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.status === 201) {
        navigate('/owner-dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit details. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="container mx-auto p-4">
        <div className="w-full lg:w-6/12 bg-white rounded-xl mx-auto shadow-lg overflow-hidden p-8">
          <h2 className="text-3xl mb-4 text-center">Owner Details</h2>
          <p className="mb-4 text-center">Please provide some additional details to complete your profile.</p>
          <form onSubmit={handleSubmit}>
            <div className="mt-5">
              <input type="text" placeholder="Full Name" className="border border-gray-400 py-2 px-4 w-full rounded" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div className="mt-5">
              <input type="text" placeholder="Organization/Company Name (Optional)" className="border border-gray-400 py-2 px-4 w-full rounded" value={organisation} onChange={(e) => setOrganisation(e.target.value)} />
            </div>
            <div className="mt-5">
              <input type="text" placeholder="Phone Number" className="border border-gray-400 py-2 px-4 w-full rounded" value={contact_No} onChange={(e) => setContact_No(e.target.value)} required />
            </div>
            <div className="mt-5">
              <input type="text" placeholder="NID Number" className="border border-gray-400 py-2 px-4 w-full rounded" value={nid_or_passport} onChange={(e) => setNid_or_passport(e.target.value)} required />
            </div>

            {/* Structured Address Inputs */}
            <div className="mt-5">
              <select className="border border-gray-400 py-2 px-4 w-full rounded" value={selectedDivision} onChange={(e) => { setSelectedDivision(e.target.value); setSelectedDistrict(''); }} required disabled={divisionsLoading}>
                <option value="">{divisionsLoading ? 'Loading...' : 'Select Division'}</option>
                {divisions.map(div => <option key={div} value={div}>{div}</option>)}
              </select>
            </div>
            <div className="mt-5">
              <select className="border border-gray-400 py-2 px-4 w-full rounded" value={selectedDistrict} onChange={(e) => { setSelectedDistrict(e.target.value); setSelectedUpazila(''); }} disabled={!selectedDivision || districtsLoading} required>
                <option value="">{districtsLoading ? 'Loading...' : 'Select District'}</option>
                {districts.map(dist => <option key={dist} value={dist}>{dist}</option>)}
              </select>
            </div>
            <div className="mt-5">
              <select className="border border-gray-400 py-2 px-4 w-full rounded" value={selectedUpazila} onChange={(e) => setSelectedUpazila(e.target.value)} disabled={!selectedDistrict || upazilasLoading} required>
                <option value="">{upazilasLoading ? 'Loading...' : 'Select Upazila'}</option>
                {upazilas.map(upz => <option key={upz} value={upz}>{upz}</option>)}
              </select>
            </div>
            <div className="mt-5">
              <input type="text" placeholder="Village / Street" className="border border-gray-400 py-2 px-4 w-full rounded" value={granularLocation} onChange={(e) => setGranularLocation(e.target.value)} disabled={!selectedUpazila} required />
            </div>
            <div className="mt-5">
              <input type="text" placeholder="Post Code" className="border border-gray-400 py-2 px-4 w-full rounded" value={postCode} onChange={(e) => setPostCode(e.target.value)} required />
            </div>
             <div className="mt-5">
              <input type="text" placeholder="Latitude (Optional)" className="border border-gray-400 py-2 px-4 w-full rounded" value={latitude} onChange={(e) => setLatitude(e.target.value)} />
            </div>
             <div className="mt-5">
              <input type="text" placeholder="Longitude (Optional)" className="border border-gray-400 py-2 px-4 w-full rounded" value={longitude} onChange={(e) => setLongitude(e.target.value)} />
            </div>

            <div className="mt-5">
              <label htmlFor="profilePicture" className="block text-gray-700 text-sm font-bold mb-2">Profile Picture (Optional)</label>
              <input type="file" id="profilePicture" accept="image/*" onChange={handleFileChange} />
              {profilePicture && (<div className="mt-4 flex justify-center"><img src={URL.createObjectURL(profilePicture)} alt="Preview" className="w-32 h-32 rounded-full object-cover" /></div>)}
            </div>

            {error && <p className="text-red-500 text-xs italic mt-4">{error}</p>}
            <div className="mt-8">
              <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded">
                Complete Registration
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default OwnerDetailsPage;
